import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Webhook,
  Plus,
  Copy,
  Check,
  Play,
  Trash2,
  Edit,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  Eye,
  EyeOff,
  Code2,
  Send,
  X,
  SlidersHorizontal,
} from 'lucide-react';

const AVAILABLE_EVENTS = [
  { id: 'lead.created', label: 'Lead Created', desc: 'Triggered when a new lead is added' },
  { id: 'lead.stage_changed', label: 'Lead Stage Changed', desc: 'Triggered when lead moves across pipeline' },
  { id: 'appointment.booked', label: 'Appointment Booked', desc: 'Triggered when a doctor appointment is scheduled' },
  { id: 'payment.completed', label: 'Payment Completed', desc: 'Triggered when a consultation or surgery payment is verified' },
];

const CRM_FIELDS = [
  { key: 'patientName', label: 'Patient Name (Required)' },
  { key: 'phone', label: 'Mobile Number (Required)' },
  { key: 'email', label: 'Email Address' },
  { key: 'city', label: 'City / Location' },
  { key: 'location', label: 'Location' },
  { key: 'category', label: 'Treatment / Category' },
  { key: 'source', label: 'Lead Source' },
  { key: 'notes', label: 'Remarks / Notes' },
];

const WebhooksManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl && ['inbound', 'outbound', 'logs'].includes(tabFromUrl) ? tabFromUrl : 'inbound');

  useEffect(() => {
    const currentTabInUrl = searchParams.get('tab');
    if (currentTabInUrl && ['inbound', 'outbound', 'logs'].includes(currentTabInUrl) && currentTabInUrl !== activeTab) {
      setActiveTab(currentTabInUrl);
    }
  }, [searchParams]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };
  const [loading, setLoading] = useState(true);
  const [inboundWebhooks, setInboundWebhooks] = useState([]);
  const [outboundWebhooks, setOutboundWebhooks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState({ direction: '', status: '' });
  const [copiedKey, setCopiedKey] = useState(null);

  // Modals state
  const [showInboundModal, setShowInboundModal] = useState(false);
  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [showLogDetailModal, setShowLogDetailModal] = useState(null);
  const [showSandboxModal, setShowSandboxModal] = useState(false);

  // Inbound form state
  const [inboundForm, setInboundForm] = useState({
    name: '',
    slug: '',
    sourceTag: 'Meta Ads',
    description: '',
    fieldMapping: [{ source: '', target: 'patientName' }],
  });

  // Outbound form state
  const [outboundForm, setOutboundForm] = useState({
    name: '',
    targetUrl: '',
    events: ['lead.created'],
  });

  // Sandbox state
  const [sandboxPayload, setSandboxPayload] = useState('{\n  "full_name": "Rohan Sharma",\n  "phone_number": "9876543210",\n  "city_name": "Mumbai",\n  "service_required": "Hair Transplant"\n}');
  const [sandboxResult, setSandboxResult] = useState(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);

  const fetchInbound = async () => {
    try {
      const res = await api.get('/webhooks/inbound');
      const list = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data?.message) ? res.data.message : []);
      setInboundWebhooks(list);
    } catch (err) {
      toast.error('Failed to load inbound webhooks');
      setInboundWebhooks([]);
    }
  };

  const fetchOutbound = async () => {
    try {
      const res = await api.get('/webhooks/outbound');
      const list = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data?.message) ? res.data.message : []);
      setOutboundWebhooks(list);
    } catch (err) {
      toast.error('Failed to load outbound webhooks');
      setOutboundWebhooks([]);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = {};
      if (logFilter.direction) params.direction = logFilter.direction;
      if (logFilter.status) params.status = logFilter.status;
      const res = await api.get('/webhooks/logs', { params });
      const rawLogs = res.data?.data?.logs || res.data?.message?.logs || (Array.isArray(res.data?.data) ? res.data.data : []);
      setLogs(Array.isArray(rawLogs) ? rawLogs : []);
    } catch (err) {
      toast.error('Failed to load webhook logs');
      setLogs([]);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchInbound(), fetchOutbound(), fetchLogs()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [logFilter, activeTab]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Inbound Handlers
  const handleSaveInbound = async (e) => {
    e.preventDefault();
    try {
      const mappingObj = {};
      inboundForm.fieldMapping.forEach(item => {
        if (item.source && item.target) {
          mappingObj[item.source.trim()] = item.target;
        }
      });

      await api.post('/webhooks/inbound', {
        name: inboundForm.name,
        slug: inboundForm.slug,
        sourceTag: inboundForm.sourceTag,
        description: inboundForm.description,
        fieldMapping: mappingObj,
      });

      toast.success('Inbound Webhook Created!');
      setShowInboundModal(false);
      setInboundForm({
        name: '',
        slug: '',
        sourceTag: 'Meta Ads',
        description: '',
        fieldMapping: [{ source: '', target: 'patientName' }],
      });
      fetchInbound();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create webhook');
    }
  };

  const handleDeleteInbound = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inbound webhook?')) return;
    try {
      await api.delete(`/webhooks/inbound/${id}`);
      toast.success('Webhook deleted');
      fetchInbound();
    } catch (err) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleToggleInboundActive = async (hook) => {
    try {
      await api.put(`/webhooks/inbound/${hook._id}`, { isActive: !hook.isActive });
      toast.success(`Webhook ${!hook.isActive ? 'activated' : 'deactivated'}`);
      fetchInbound();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Outbound Handlers
  const handleSaveOutbound = async (e) => {
    e.preventDefault();
    try {
      if (!outboundForm.events.length) {
        toast.error('Please select at least one event');
        return;
      }

      await api.post('/webhooks/outbound', outboundForm);
      toast.success('Outbound Webhook Registered!');
      setShowOutboundModal(false);
      setOutboundForm({ name: '', targetUrl: '', events: ['lead.created'] });
      fetchOutbound();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create outbound webhook');
    }
  };

  const handleDeleteOutbound = async (id) => {
    if (!window.confirm('Are you sure you want to delete this outbound webhook?')) return;
    try {
      await api.delete(`/webhooks/outbound/${id}`);
      toast.success('Outbound webhook removed');
      fetchOutbound();
    } catch (err) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTestOutbound = async (hook) => {
    const toastId = toast.loading(`Sending test ping to ${hook.name}...`);
    try {
      const res = await api.post(`/webhooks/outbound/${hook._id}/test`, {
        event: hook.events[0] || 'lead.created',
      });
      const result = res.data.data;
      if (result.success) {
        toast.success(`Ping Successful! HTTP ${result.responseStatus} (${result.durationMs}ms)`, { id: toastId });
      } else {
        toast.error(`Ping Failed: ${result.error || `HTTP ${result.responseStatus}`}`, { id: toastId });
      }
      fetchOutbound();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Ping failed to send', { id: toastId });
    }
  };

  // Sandbox simulation test
  const handleRunSandbox = async () => {
    setSandboxLoading(true);
    try {
      let parsed = {};
      try {
        parsed = JSON.parse(sandboxPayload);
      } catch (e) {
        toast.error('Invalid JSON format in payload');
        setSandboxLoading(false);
        return;
      }

      const mappingObj = {};
      inboundForm.fieldMapping.forEach(item => {
        if (item.source && item.target) {
          mappingObj[item.source.trim()] = item.target;
        }
      });

      const res = await api.post('/webhooks/simulate', {
        payload: parsed,
        fieldMapping: mappingObj,
      });

      setSandboxResult(res.data.data);
      toast.success('Simulation completed!');
    } catch (err) {
      toast.error('Simulation error: ' + (err.response?.data?.message || err.message));
    } finally {
      setSandboxLoading(false);
    }
  };

  const baseUrl = window.location.origin;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#143E78] to-[#1e5aa8] rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/20 shadow-inner">
              <Webhook className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Webhooks & API Integrations</h1>
          </div>
          <p className="text-blue-100 text-sm max-w-2xl font-medium leading-relaxed">
            Enterprise Event Hub. Ingest leads instantly from Meta Ads, Google, and Forms, or sync surgery and payments directly to external Hospital HIS and Accounting systems.
          </p>
        </div>

        <div className="flex items-center gap-3 self-start md:self-auto">
          {activeTab === 'inbound' ? (
            <button
              onClick={() => setShowInboundModal(true)}
              className="bg-white text-[#143E78] px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-blue-50 transition-all shadow-lg active:scale-95 flex items-center gap-2"
            >
              <Plus size={16} /> New Inbound Hook
            </button>
          ) : activeTab === 'outbound' ? (
            <button
              onClick={() => setShowOutboundModal(true)}
              className="bg-white text-[#143E78] px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-wider hover:bg-blue-50 transition-all shadow-lg active:scale-95 flex items-center gap-2"
            >
              <Plus size={16} /> New Outbound Hook
            </button>
          ) : (
            <button
              onClick={fetchLogs}
              className="bg-white/10 hover:bg-white/20 text-white px-4 py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2"
            >
              <RefreshCw size={14} /> Refresh Logs
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex border-b border-gray-200 gap-8">
        <button
          onClick={() => handleTabChange('inbound')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all relative ${
            activeTab === 'inbound'
              ? 'text-[#143E78] border-b-2 border-[#143E78]'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <ArrowDownLeft size={18} /> Inbound Webhooks (Lead Ingestion)
          <span className="ml-1 px-2 py-0.5 text-[10px] font-black rounded-full bg-blue-100 text-blue-700">
            {Array.isArray(inboundWebhooks) ? inboundWebhooks.length : 0}
          </span>
        </button>

        <button
          onClick={() => handleTabChange('outbound')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all relative ${
            activeTab === 'outbound'
              ? 'text-[#143E78] border-b-2 border-[#143E78]'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <ArrowUpRight size={18} /> Outbound Webhooks (Event Dispatcher)
          <span className="ml-1 px-2 py-0.5 text-[10px] font-black rounded-full bg-emerald-100 text-emerald-700">
            {Array.isArray(outboundWebhooks) ? outboundWebhooks.length : 0}
          </span>
        </button>

        <button
          onClick={() => handleTabChange('logs')}
          className={`pb-4 text-sm font-bold flex items-center gap-2 transition-all relative ${
            activeTab === 'logs'
              ? 'text-[#143E78] border-b-2 border-[#143E78]'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <Clock size={18} /> Delivery & Audit Logs
        </button>
      </div>

      {/* TAB 1: INBOUND WEBHOOKS */}
      {activeTab === 'inbound' && (
        <div className="space-y-4">
          {(!Array.isArray(inboundWebhooks) || inboundWebhooks.length === 0) && !loading ? (
            <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center space-y-4 shadow-sm">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto">
                <ArrowDownLeft size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">No Inbound Webhooks Configured</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Create your first inbound hook to receive real-time leads from Meta Lead Ads, Google Ads forms, or your website.
              </p>
              <button
                onClick={() => setShowInboundModal(true)}
                className="bg-[#143E78] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-[#0f2c56] transition-all"
              >
                Create Inbound Webhook
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {(Array.isArray(inboundWebhooks) ? inboundWebhooks : []).map((hook) => {
                const endpointUrl = `${baseUrl}/api/webhooks/inbound/${hook.slug}`;
                return (
                  <div
                    key={hook._id}
                    className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${hook.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
                        <h3 className="text-base font-bold text-gray-900">{hook.name}</h3>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100">
                          {hook.sourceTag || 'Inbound'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleInboundActive(hook)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-bold transition-all ${
                            hook.isActive
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {hook.isActive ? 'Active' : 'Paused'}
                        </button>
                        <button
                          onClick={() => handleDeleteInbound(hook._id)}
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Endpoint URL & Auth Details */}
                    <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Webhook URL</span>
                        <button
                          onClick={() => copyToClipboard(endpointUrl, hook._id)}
                          className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1"
                        >
                          {copiedKey === hook._id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                          {copiedKey === hook._id ? 'Copied' : 'Copy URL'}
                        </button>
                      </div>
                      <code className="block text-xs font-mono text-slate-800 break-all select-all">
                        {endpointUrl}
                      </code>

                      <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-600 border-t border-slate-200 gap-2">
                        <div>
                          <span className="font-bold text-slate-400 uppercase text-[10px] mr-1">Header Auth:</span>
                          <code className="bg-white px-2 py-0.5 rounded border border-slate-200 font-mono text-[11px]">
                            X-Webhook-Key: {hook.apiKey}
                          </code>
                        </div>
                        <div className="text-[11px] text-slate-400 font-medium">
                          Total Received: <strong className="text-slate-700 font-black">{hook.totalReceived || 0} leads</strong>
                          {hook.lastReceivedAt && (
                            <span className="ml-2">• Last: {new Date(hook.lastReceivedAt).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: OUTBOUND WEBHOOKS */}
      {activeTab === 'outbound' && (
        <div className="space-y-4">
          {(!Array.isArray(outboundWebhooks) || outboundWebhooks.length === 0) && !loading ? (
            <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center space-y-4 shadow-sm">
              <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <ArrowUpRight size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">No Outbound Webhooks Registered</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Send real-time alerts to Hospital HIS, Tally/Zoho accounting, or partner APIs whenever a lead changes stage or a payment is verified.
              </p>
              <button
                onClick={() => setShowOutboundModal(true)}
                className="bg-[#143E78] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-[#0f2c56] transition-all"
              >
                Register Outbound Webhook
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {(Array.isArray(outboundWebhooks) ? outboundWebhooks : []).map((hook) => (
                <div
                  key={hook._id}
                  className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all space-y-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${hook.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                      <h3 className="text-base font-bold text-gray-900">{hook.name}</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {hook.events?.map((ev) => (
                          <span
                            key={ev}
                            className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-700"
                          >
                            {ev}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTestOutbound(hook)}
                        className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                      >
                        <Play size={12} fill="currentColor" /> Test Ping
                      </button>
                      <button
                        onClick={() => handleDeleteOutbound(hook._id)}
                        className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Target URL & HMAC Secret */}
                  <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Target HTTPS URL</span>
                    </div>
                    <code className="block text-xs font-mono text-slate-800 break-all select-all">
                      {hook.targetUrl}
                    </code>

                    <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-600 border-t border-slate-200 gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-400 uppercase text-[10px]">HMAC Secret:</span>
                        <code className="bg-white px-2 py-0.5 rounded border border-slate-200 font-mono text-[11px] text-slate-600">
                          {hook.secretKey ? `${hook.secretKey.slice(0, 8)}••••••••` : 'None'}
                        </code>
                        <button
                          onClick={() => copyToClipboard(hook.secretKey, hook._id + '_sec')}
                          className="text-blue-600 hover:text-blue-800 text-[11px] font-bold"
                        >
                          {copiedKey === hook._id + '_sec' ? 'Copied' : 'Copy'}
                        </button>
                      </div>

                      <div className="text-[11px] text-slate-400 font-medium flex items-center gap-3">
                        <span>Dispatched: <strong className="text-slate-700 font-black">{hook.totalDispatched || 0}</strong></span>
                        {hook.lastStatusCode ? (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black ${
                            hook.lastStatusCode >= 200 && hook.lastStatusCode < 300
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-rose-100 text-rose-700'
                          }`}>
                            HTTP {hook.lastStatusCode}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: AUDIT & DELIVERY LOGS */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <SlidersHorizontal size={16} className="text-gray-400" />
              <select
                value={logFilter.direction}
                onChange={(e) => setLogFilter({ ...logFilter, direction: e.target.value })}
                className="text-xs font-bold border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 focus:outline-none"
              >
                <option value="">All Directions</option>
                <option value="inbound">Inbound (Incoming)</option>
                <option value="outbound">Outbound (Outgoing)</option>
              </select>

              <select
                value={logFilter.status}
                onChange={(e) => setLogFilter({ ...logFilter, status: e.target.value })}
                className="text-xs font-bold border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 focus:outline-none"
              >
                <option value="">All Statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <span className="text-xs text-gray-400 font-medium">
              Showing {logs.length} recent delivery attempts
            </span>
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider font-black text-[10px]">
                  <tr>
                    <th className="px-5 py-3.5">Direction</th>
                    <th className="px-5 py-3.5">Event</th>
                    <th className="px-5 py-3.5">Target / Origin</th>
                    <th className="px-5 py-3.5">HTTP Status</th>
                    <th className="px-5 py-3.5">Duration</th>
                    <th className="px-5 py-3.5">Timestamp</th>
                    <th className="px-5 py-3.5 text-right">Payload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(!Array.isArray(logs) || logs.length === 0) ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-gray-400 font-medium">
                        No logs recorded yet.
                      </td>
                    </tr>
                  ) : (
                    (Array.isArray(logs) ? logs : []).map((log) => (
                      <tr key={log._id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                              log.direction === 'inbound'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            {log.direction}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 font-bold text-slate-800">{log.event}</td>
                        <td className="px-5 py-3.5 text-slate-600 font-mono text-[11px] max-w-xs truncate">
                          {log.url}
                        </td>
                        <td className="px-5 py-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-md font-black text-[10px] ${
                              log.status === 'success'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}
                          >
                            {log.responseStatus ? `HTTP ${log.responseStatus}` : log.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-slate-500 font-mono">{log.durationMs}ms</td>
                        <td className="px-5 py-3.5 text-slate-500">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <button
                            onClick={() => setShowLogDetailModal(log)}
                            className="text-blue-600 hover:text-blue-800 font-bold hover:underline"
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CREATE INBOUND WEBHOOK */}
      {showInboundModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl border border-gray-100 space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
                  <ArrowDownLeft size={20} />
                </div>
                <h2 className="text-lg font-black text-gray-900">New Inbound Webhook</h2>
              </div>
              <button onClick={() => setShowInboundModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveInbound} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 uppercase tracking-wider mb-1">Webhook Name *</label>
                <input
                  type="text"
                  required
                  value={inboundForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
                    setInboundForm({ ...inboundForm, name, slug: inboundForm.slug || slug });
                  }}
                  placeholder="e.g. Meta Ads - Hair Transplant"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 uppercase tracking-wider mb-1">URL Slug *</label>
                  <input
                    type="text"
                    required
                    value={inboundForm.slug}
                    onChange={(e) => setInboundForm({ ...inboundForm, slug: e.target.value })}
                    placeholder="e.g. meta-hair-ads"
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 uppercase tracking-wider mb-1">Source Tag</label>
                  <input
                    type="text"
                    value={inboundForm.sourceTag}
                    onChange={(e) => setInboundForm({ ...inboundForm, sourceTag: e.target.value })}
                    placeholder="e.g. Meta Ads, Landing Page"
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Dynamic Field Mapping Editor */}
              <div className="pt-2 border-t border-gray-100 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="font-black text-gray-800 uppercase tracking-wider text-[11px]">
                    Field Mapping (Incoming JSON ➔ CRM Lead)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setInboundForm({
                        ...inboundForm,
                        fieldMapping: [...inboundForm.fieldMapping, { source: '', target: 'phone' }],
                      });
                    }}
                    className="text-blue-600 hover:text-blue-800 font-bold text-xs flex items-center gap-1"
                  >
                    <Plus size={12} /> Add Field
                  </button>
                </div>

                <div className="space-y-2">
                  {inboundForm.fieldMapping.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Incoming key (e.g. full_name)"
                        value={item.source}
                        onChange={(e) => {
                          const updated = [...inboundForm.fieldMapping];
                          updated[idx].source = e.target.value;
                          setInboundForm({ ...inboundForm, fieldMapping: updated });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-xl font-mono text-xs"
                      />
                      <span className="text-gray-400 font-bold">➔</span>
                      <select
                        value={item.target}
                        onChange={(e) => {
                          const updated = [...inboundForm.fieldMapping];
                          updated[idx].target = e.target.value;
                          setInboundForm({ ...inboundForm, fieldMapping: updated });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-xl font-bold bg-white text-xs"
                      >
                        {CRM_FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      {inboundForm.fieldMapping.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = inboundForm.fieldMapping.filter((_, i) => i !== idx);
                            setInboundForm({ ...inboundForm, fieldMapping: updated });
                          }}
                          className="text-gray-400 hover:text-rose-500 p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowInboundModal(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#143E78] text-white rounded-xl font-bold hover:bg-[#0f2c56] shadow-md transition-all"
                >
                  Save & Generate URL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CREATE OUTBOUND WEBHOOK */}
      {showOutboundModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl border border-gray-100 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                  <ArrowUpRight size={20} />
                </div>
                <h2 className="text-lg font-black text-gray-900">New Outbound Webhook</h2>
              </div>
              <button onClick={() => setShowOutboundModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveOutbound} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 uppercase tracking-wider mb-1">Integration Name *</label>
                <input
                  type="text"
                  required
                  value={outboundForm.name}
                  onChange={(e) => setOutboundForm({ ...outboundForm, name: e.target.value })}
                  placeholder="e.g. Apollo Hospital HIS Sync, Tally ERP"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 uppercase tracking-wider mb-1">Target HTTPS Endpoint URL *</label>
                <input
                  type="url"
                  required
                  value={outboundForm.targetUrl}
                  onChange={(e) => setOutboundForm({ ...outboundForm, targetUrl: e.target.value })}
                  placeholder="https://partner-system.com/api/receive-event"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl font-mono text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-black text-gray-800 uppercase tracking-wider text-[11px] mb-2">
                  Subscribe to Events *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {AVAILABLE_EVENTS.map((ev) => {
                    const isChecked = outboundForm.events.includes(ev.id);
                    return (
                      <label
                        key={ev.id}
                        className={`p-3 rounded-xl border flex items-start gap-2.5 cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-emerald-50/50 border-emerald-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setOutboundForm({ ...outboundForm, events: [...outboundForm.events, ev.id] });
                            } else {
                              setOutboundForm({
                                ...outboundForm,
                                events: outboundForm.events.filter((id) => id !== ev.id),
                              });
                            }
                          }}
                          className="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500"
                        />
                        <div>
                          <p className="font-bold text-gray-900">{ev.label}</p>
                          <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{ev.desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowOutboundModal(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 shadow-md transition-all"
                >
                  Register Webhook
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: LOG INSPECT DRAWER */}
      {showLogDetailModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl border border-gray-100 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-black text-gray-900">
                Webhook Execution Detail: {showLogDetailModal.event}
              </h2>
              <button onClick={() => setShowLogDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-400 uppercase text-[10px]">Target URL:</span>
                <code className="bg-slate-100 px-2 py-0.5 rounded font-mono text-[11px] break-all">
                  {showLogDetailModal.url}
                </code>
              </div>

              <div>
                <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                  Sent / Received Payload (JSON)
                </span>
                <pre className="bg-slate-900 text-emerald-400 p-3.5 rounded-xl font-mono text-[11px] max-h-48 overflow-y-auto">
                  {JSON.stringify(showLogDetailModal.payload, null, 2)}
                </pre>
              </div>

              <div>
                <span className="block font-bold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                  Response Body (HTTP {showLogDetailModal.responseStatus})
                </span>
                <pre className="bg-slate-900 text-blue-300 p-3.5 rounded-xl font-mono text-[11px] max-h-48 overflow-y-auto">
                  {JSON.stringify(showLogDetailModal.responseBody, null, 2)}
                </pre>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowLogDetailModal(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksManagement;
